import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Stage, Layer, Rect, Group } from 'react-konva';
import { Howl } from 'howler';
import { getAverageRGB, generateUUID, imgPath, getSoundPropsById } from '../../lib/ep.js';

import Spinner from '../builder/Spinner';
import Konva from 'konva';

const standard_board_size = 600;	// px

const CrushCanvas = forwardRef((props, ref) => {
	const stage = (props.stage != undefined) ? props.stage : {};
	const is_builder = (props.is_builder != undefined) ? props.is_builder : false;
	const is_test = (props.is_test != undefined) ? props.is_test : false;
	const is_playable = (props.is_playable != undefined) ? props.is_playable : ((!is_builder || is_test) ? true : false);

	const board_size = 8;
	const stroke_anim_duration = 0.55;
	const multiplier_linecombo = 1;
	const multiplier_ltcombo = 2;
	const multiplier_crosscombo = 3;
	const debug = false;
	const hitsRef = props.hitsRef;
	const hint_group = true; // Group of posible combination, hint
	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	let loaded_images = {};

	const initial_square = [];
	for(let i = 0; i < board_size; i++) initial_square[i] = [];
	
	const [ items, setItems ] = useState([]);
	const [ successful, setSuccessful ] = useState([]);
	const [ full_loaded, setFullLoaded ] = useState(false);

	const size = useRef(standard_board_size);
	const actual = useRef(null);
	const actual_over = useRef(null);
	const candy_index = useRef(0);
	const candy_size = useRef(size.current / board_size);
	const counter_images = useRef(0);
	const display_hint = useRef(null);
	const hinted_candies = useRef(null);
	const last_combo = useRef(null);
	const moving = useRef(0);
	const refs = useRef([]);
	const scoring = useRef(false);
	const squareRef = useRef(initial_square);
	const validMoves = useRef(0);
	const is_idle = useRef(true);
	const stoppedGame = useRef(false);

	const total_elements = stage?.crush_level ?? 5;
	const colors = (stage.crush_images != undefined) ? (stage.crush_images).slice(0, total_elements) : [];
	const display_outter_border = stage?.display_outter_border ?? true;

	const preventLeaving = (e) => {
		e.returnValue = "There are unsaved changes. Leave now?";
	};

	useEffect(() => {
		loadImages();
	
		if(!is_builder) window.addEventListener('beforeunload', preventLeaving);

		return () => {
			if(props.endSoundtrack != undefined) props.endSoundtrack(true);

			exitGame();
			finishHintAnimation();
			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);
		}
	}, []);

	useEffect(() => {
		if(!full_loaded) return;

		squareRef.current.map((row) => {
			row.map((sq) => {
				refs.current[`candy_${sq.id}`].cache(default_cache_props);
			});
		});

		if(refs.current['layer'] != undefined) refs.current['layer'].draw();
	}, [ full_loaded ]);

	useImperativeHandle(ref, () => ({
		stopGame(success_game, cb) {
			stoppedGame.current = true;

			if(!is_builder) window.removeEventListener('beforeunload', preventLeaving);

			const cleanup = () => {
				if(!is_idle.current) {
					setTimeout(cleanup, 500);
					return;
				}

				exitGame();

				if(cb != undefined) {
					setTimeout(() => {
						cb();
					}, 200);
				}
			}
			cleanup();
		},
	}), []);

	/* SOUNDS - ini */
	const sound_slide = new Howl({ ...getSoundPropsById('slide') });
	const soundSlide = () => sound_slide.play();

	const sound_cross = new Howl({ ...getSoundPropsById('cross') });
	const soundCross = () => sound_cross.play();

	const sound_hint = new Howl({ ...getSoundPropsById('hint') });
	const soundHint = () => sound_hint.play();
	/* SOUNDS - end */

	const exitGame = () => {
		if(refs.current[`main_stage`]) {
			refs.current[`main_stage`].destroyChildren();
			refs.current[`main_stage`].destroy();
		}
	};

	const loadImages = () => {
		colors.map((color) => {
			counter_images.current++;
			loaded_images[color.id] = new window.Image();
			loaded_images[color.id].crossOrigin = 'Anonymous';
			let full_image = imgPath(EP.BUCKET,stage.promotion_id,'stages') + color.image;
			loaded_images[color.id].src = full_image;
			loaded_images[color.id].onload = () => {
				handleLoad(loaded_images[color.id], color.id);
			};
		});
	};

	const handleLoad = (image, index) => {
		var image = loaded_images[index];

		const new_image = {
			fillPatternImage:image,
			image_w:image.width,
			image_h:image.height,
			size_piece_w:image.width / board_size,
			size_piece_h:image.height / board_size,
			identifier:index,
			av_color:getAverageRGB(image),
		};

		let image_items = items;
		image_items.push(new_image);
		setItems(image_items);

		counter_images.current--;
		if(counter_images.current == 0) gameInit();
	};

	const gameInit = (from_resize) => {
		if(from_resize == undefined) from_resize = false;
		if(props.adaptToContainer != undefined) props.adaptToContainer((size_received) => {
			size.current = size_received;
			candy_size.current = size_received / board_size;
			prepareNewGame();
			setFullLoaded(true);
			props.startTimer();
		});
	};

	/*
	 * USEFUL FOR DEBUGGING
	 * Specify a board configuration by passing in a boardSpec. The format
	 * of boardSpec is a list of strings, one sequence for each row. In each
	 * string, there must be boardSize characters, where each character should
	 * be the first letter of the color for that square. For example, a boardSpec
	 * that specifies an 8x8 board with alternating columns of red and orange would have
	 * a boardSpec of:
	 *	['rorororo',
	 *	 'rorororo',
	 *	 'rorororo',
	 *	 'rorororo',
	 *	 'rorororo',
	 *	 'rorororo',
	 *	 'rorororo',
	 *	 'rorororo']
	 */
	const createSpecifiedBoard = (boardSpec) => {
		let numChars = 0;

		boardSpec.map((i) => { return numChars += i.length; });
		if(boardSpec.length != board_size || numChars != Math.pow(board_size, 2)) {
			console.warn("boardSpec must be of dimensions boardSize x boardSize to populate board");
			return;
		}

		for(let col = 0; col < board_size; col++) {
			for(let row = 0; row < board_size; row++) {
				if(getCandyAt(row, col) == null) {
					let color = boardSpec[row].charAt(col);
					addCandy(color, row, col);
				}
			}
		}
	};

	/*
	 * Prepares a new game with no groups of three adjacent same-color candies.
	 * Any time there is a group of adjacent same-color candies, re-rolls them.
	 * Sets the score to zero such that the player does not get points for
	 * crushes that occure by luck initially.
	 */
	const prepareNewGame = () => {
		scoring.current = false;
		while(true) {
			populateBoard();
			var crushable = getCandyCrushes();
			if(crushable.length == 0) break;
			removeCrushes(crushable);
		}
		displayHint();

		scoring.current = true;
	};

	/*
	*  Returns true if flipping fromCandy with the candy in the direction
	*  specified (['up', 'down', 'left', 'right']) is valid
	*  (according to the rules), else returns false.
	*/
	const isMoveTypeValid = (fromCandy, direction) => {
		return numberCandiesCrushedByMove(fromCandy, direction).length > 0;
	};

	/*
	*  Returns a list of ALL candy crushes on the board. A candy crush is
	*  a list of three or more candies in a single row or column that have
	*  the same color. Each crush is provided as a list of the candies being
	*  crushed, resulting in a list of lists. The output of this method should
	*  be passed directly into this.removeCrushes to remove candy crushes.
	*/
	const getCandyCrushes = (swap) => {
		// Implemented with a (not fully optimized) Tarjan's union-find algorithm.
		// Implementation of the classic union-find algorithm (unoptimized).
		// Allows any string keys to be unioned into a set of disjoint sets.
		// https://en.wikipedia.org/wiki/Disjoint-set_data_structure
		let unioned = {};
		let sizes = {};
		let row, col;

		// Finds the set representative for the set that this key is a member of.
		const find = (key) =>{
			let parent = unioned[key];
			if(parent == null) return key;
			parent = find(parent);
			unioned[key] = parent;  // Path compression
			return parent;
		};

		// The size of the set represented by 'found'; assume 1 if not stored.
		const size = (found) => {
			return sizes[found] || 1;
		};

		// Ennsures that the two keys are in the same set, joining if needed.
		const union = (key1, key2) => {
			var p1 = find(key1), p2 = find(key2);
			if (p1 == p2) return p1;

			// Do not bother implementing union by rank.  This is pretty fast too.
			// n.b., http://stackoverflow.com/a/2326676/265298
			unioned[p2] = p1;
			sizes[p1] = size(p1) + size(p2);
			delete sizes[p2];
		};

		// Get strips of length 3.
		var vert = findColorStrips(true, swap);
		var horiz = findColorStrips(false, swap);
		var sets = vert.concat(horiz);

		// Execute union of all the strips, possibly joining
		// horizontal and vertical strips that intersect.
		for(var j = 0; j < sets.length; j++) {
			var set = sets[j];
			for(var k = 1; k < set.length; k++) {
				union(set[0].id, set[k].id)
			}
		}

		// Pass 2: list out resulting sets of minSize or larger.
		var results = {}
		for(row = 0; row < board_size; row++) {
			for(col = 0; col < board_size; col++) {
				var candy = getCandyAt(row, col);
				if(candy) {
					var p = find(candy.id);
					if(size(p) >= 3) {
						if(!(p in results)) results[p] = [];
						results[p].push(candy);
					}
				}
			}
		}

		// Pass 3: Return results as a list of list of candies.
		var list = [];
		for(var key in results) {
			list.push(results[key]);
		}

		return list;
	};

	/*
	*  Deletes all the candies in setOfSetsOfCrushes (which can be
	*  generated by getCandyCrushes or by getCandiesToCrushGivenMove.)
	*  Does not shift candies down at all. Updates the score accordingly.
	*/
	const removeCrushes = (setOfSetsOfCrushes) => {
		for(var j = 0; j < setOfSetsOfCrushes.length; j++) {
			let set = setOfSetsOfCrushes[j];

			for(var k = 0; k < set.length; k++) {
				if(scoring.current) incrementScore(set[k]);
				remove(set[k]);
			}
		}
	};

	/*
	*  Moves candies down as far as there are empty spaces. Issues calls to
	*  board.moveTo, which generate "move" events to listen for. If there
	*  are holes created by moving the candies down, populates the holes
	*  with random candies, and issues "add" events for these candies.
	*/
	const moveCandiesDown = () => {
		// Collapse each column
		for(var col = 0; col < board_size; col++) {
			var emptyRow = null;
			// In each column, scan for the bottom most empty row
			for(var emptyRow = board_size - 1; emptyRow >= 0; emptyRow--) {
				if(!getCandyAt(emptyRow, col)) break;
			}

			// Then shift any nonempty rows up
			for(var row = emptyRow - 1; row >= 0; row--) {
				var candy = getCandyAt(row, col);
				if(candy) {
					moveTo(candy, emptyRow, col);
					emptyRow--;
				}
			}

			for(var spawnRow = -1; emptyRow >= 0; emptyRow--, spawnRow--) {
				// We report spawnRow as the (negative) position where
				// the candy "would have" started to fall into place.
				addRandomCandy(emptyRow, col, spawnRow, col);
			}
		}
	};

	/*
	*  If there is a valid move, returns an object with two properties:
	*  candy: a Candy that can be moved
	*  direction: the direction that it can be moved.
	*  If there are no valid moves, returns null.  The move is selected
	*  randomly from the available moves, favoring moves with smaller crushes.
	*/
	const getRandomValidMove = () => {
		const directions = ['up', 'down', 'left', 'right'];
		let validMovesThreeCrush = [];
		let validMovesGroupThreeCrush = [];
		let validMovesMoreThanThreeCrush = [];
		let validMovesGroupMoreThanThreeCrush = [];
		validMoves.current = 0;
		// For each cell in the board, check to see if moving it in
		// any of the four directions would result in a crush
		// if so, add it to the appropriate list (validMoves_threeCrush for
		// crushes of size 3, validMoves_moreThanThreeCrush for crushes
		// larger than 3)
		for(let row = 0; row < board_size; row++) {
			for(let col = 0; col < board_size; col++) {
				let fromCandy = getCandyAt(row, col);
				if(!fromCandy) continue;

				for(i = 0; i < 4; i++) {
					let direction = directions[i];
					let candiesCrushed = numberCandiesCrushedByMove(fromCandy, direction);
					let numCandiesCrushed = candiesCrushed.length;
					if(numCandiesCrushed == 3) {
						if(hint_group) {
							validMovesGroupThreeCrush.push(candiesCrushed);
							validMoves.current++;
						} else {
							if(fromCandy.color == candiesCrushed[0].color) {
								validMovesThreeCrush.push({ candy:fromCandy, direction });
								validMoves.current++;
							}
						}
					} else if(numCandiesCrushed > 3) {
						if(hint_group) {
							validMovesGroupMoreThanThreeCrush.push(candiesCrushed);
						} else {
							if(fromCandy.color == candiesCrushed[0].color) {
								validMovesMoreThanThreeCrush.push({ candy:fromCandy, direction });
								validMoves.current++;
							}
						}
					}
				}
			}
		}

		// if there are three-crushes possible, prioritize these
		if(hint_group) {
			var searchArray = (validMovesGroupThreeCrush.length) ? validMovesGroupThreeCrush : validMovesGroupMoreThanThreeCrush;
		} else {
			var searchArray = (validMovesThreeCrush.length) ? validMovesThreeCrush : validMovesMoreThanThreeCrush;
		}

		// If there are no valid moves, return null.
		if(searchArray.length == 0) return null;

		// select a random crush from among the crushes found
		return searchArray[Math.floor(Math.random() * searchArray.length)];
	};

	/*
	*  Helper method to rules.prepareNewGame
	*  Called when a new game is created. Fills all the empty positions on
	*  the board with random-colored candies.
	*/
	const populateBoard = () => {
		for(var col = 0; col < board_size; col++) {
			for(var row = 0; row < board_size; row++) {
				// Check the empty candy position (hole), fill with new candy
				if(getCandyAt(row, col) == null) addRandomCandy(row, col);
			}
		}
	};

	/*
	*  Helper method for rules.isMoveTypeValid
	*  Returns the number of candies that would be crushed if the candy
	*  provided by fromCandy were to be flipped in the direction
	*  specified (['up', 'down', 'left', 'right'])
	*
	*  If this move is not valid (based on the game rules), then 0 is returned
	*/
	const numberCandiesCrushedByMove = (fromCandy, direction) => {
		return getCandiesToCrushGivenMove(fromCandy, direction);
	};

	/*
	*  Helper method for rules.numberCandiesCrushedByMove
	*  Returns a list of candies that would be "crushed" (i.e. removed) if
	*  fromCandy were to be moved in the direction specified by direction (['up',
	*  'down', 'left', 'right'])
	*  If move would result in no crushed candies, an empty list is returned.
	*/
	const getCandiesToCrushGivenMove = (fromCandy, direction) => {
		let toCandy = getCandyInDirection(fromCandy, direction);
		if(!toCandy || toCandy.color == fromCandy.color) return [];
		const swap = [fromCandy, toCandy];
		const crushable = getCandyCrushes(swap);

		// Only return crushable groups that involve the swapped candies.
		// If the board has incompletely-resolved crushes, there can be
		// many crushable candies that are not touching the swapped ones.
		const connected = crushable.filter((set) => {
			for(let k = 0; k < swap.length; k++) {
				if(set.indexOf(swap[k]) >= 0) return true;
			}

			return false;
		});

		return [].concat.apply([], connected); //flatten nested lists
	};

	/*
	*  Helper Method for rules.getCandyCrushes
	*  Returns a set of sets of all the same-color candy strips of length
	*  at least 3 on the board.  If 'vertical' is set to true, looks only for
	*  vertical strips; otherwise only horizontal ones. If the 'swap' array
	*  is passed, then every even-indexed candy in the array is considered
	*  swapped with every odd-indexed candy in the array.
	*/
	const findColorStrips = (vertical, swap) => {
		const getAt = (x, y) => {
			// Retrieve the candy at a row and column (depending on vertical)
			const result = (vertical) ? getCandyAt(y, x) : getCandyAt(x, y);
			if(swap) {
				// If the result candy is in the 'swap' array, then swap the result with its adjacent pair.
				let index = swap.indexOf(result);
				if(index >= 0) return swap[index ^ 1];
			}

			return result;
		};

		let result = [];
		for(let j = 0; j < board_size; j++) {
			for(let h, k = 0; k < board_size; k = h) {
				// Scan for rows of same-colored candy starting at k
				var firstCandy = getAt(j, k);
				h = k + 1;
				if(!firstCandy) continue;
				let candies = [firstCandy];
				for(; h < board_size; h++) {
					const lastCandy = getAt(j, h);
					if(!lastCandy || lastCandy.color != firstCandy.color) break;
					candies.push(lastCandy);
				}
				// If there are at least 3 candies in a row, remember the set.
				if(candies.length >= 3) result.push(candies);
			}
		}

		return result;
	};

	/*
	 * Returns true/false depending on whether row and column
	 * identify a valid square on the board.
	 */
	const isValidLocation = (row, col) => {
		return (row >= 0 && col >= 0 &&
			row <= board_size && col <= board_size &&
			row == Math.round(row) && col == Math.round(col));
	};

	/*
	 * Returns true/false depending on whether the square at [row,col] is empty (does not contain a candy).
	 */
	const isEmptyLocation = (row, col) => {
		if(getCandyAt(row, col)) return false;
		return true;
	};

	/**
	 * Get the candy found on the square at [row,column], or null if the square is empty.  Requires row,column < size.
	 */
	const getCandyAt = (row, col) => {
		if(isValidLocation(row, col) && squareRef.current[row] != undefined && squareRef.current[row][col] != undefined) {
			return squareRef.current[row][col];
		}
	};

	/*
	* Add a new candy to the board.  Requires candies to be not currently
	* on the board, and (row,col) must designate a valid empty square.
	*
	* The optional spawnRow, spawnCol indicate where the candy
	* was "spawned" the moment before it moved to row, col. This location,
	* which may be off the board, is added to the 'add' event and
	* can be used to animate new candies that are coming in from offscreen.
	*/
	const add = (candy, row, col, spawnRow, spawnCol) => {
		if(isEmptyLocation(row, col)) {
			candy.row = row;
			candy.col = col;
			squareRef.current[row][col] = candy;
			if(full_loaded) {
				addMove({
					candy:candy,
					toRow:row,
					toCol:col,
					fromRow:spawnRow,
					fromCol:spawnCol
				});
			}
		}
	};

	const addMove = (info) => {
		const layer = refs.current['layer'];
		const candies_group = refs.current['candies_group'];
		let candy = info.candy;
		const top = candy.row * candy_size.current;
		const left = candy.col * candy_size.current;

		let image = items.find((item) => item.identifier == candy.color);
		const slide_size = candy_size.current / (Math.max(image.image_w, image.image_h));

		let startTop = top  - size.current / 2.5;

		const candy_group = new Konva.Group({
			key:`candy_${candy.id}`,
			scale:{ x:1, y:1 },
			draggable:is_playable,
			x:left + candy_size.current / 2,
			y:startTop + candy_size.current / 2,
			width:candy_size.current,
			height:candy_size.current,
			offsetX:candy_size.current / 2,
			offsetY:candy_size.current / 2,
		});

		const rect = new Konva.Rect({
			width:candy_size.current,
			height:candy_size.current,
			id:`candy_${candy.id}`,
			fillPatternImage:image.fillPatternImage,
			fillPatternRepeat:'no-repeat',
			fillPatternScaleX:slide_size,
			fillPatternScaleY:slide_size,
		});
		candy_group.add(rect);

		candy_group.on('touchend mouseup dragend', handleDragEnd);
		candy_group.on('mousedown touchstart', handleDragStart);
		if(candies_group != undefined) candies_group.add(candy_group);

		moving.current++;

		candy_group.to({
			y:top + candy_size.current / 2,
			easing:Konva.Easings.BounceEaseOut,
			duration:stroke_anim_duration,
			onFinish:() => {
				moving.current--;
				if(moving.current == 0) Crush();
			},
		});

		refs.current[`candy_${candy.id}`] = candy_group;
		refs.current[`candy_${candy.id}`].cache(default_cache_props);
		if(layer != undefined) layer.draw();
	};

	/**
	* Move a candy from its current square to another square.
	* Requires candy to be already found on this board, and (toRow,toCol)
	* must denote a valid empty square.
	*/
	const moveTo = (candy, toRow, toCol) => {
		if(isEmptyLocation(toRow, toCol)) {
			if(squareRef.current[candy.row] != undefined && squareRef.current[candy.row][candy.col] != undefined) {
				delete squareRef.current[candy.row][candy.col];

				squareRef.current[toRow][toCol] = {
					color:candy.color,
					row:toRow,
					col:toCol,
					id:candy.id,
				};

				moveAnimate({
					candy:candy,
					toRow:toRow,
					toCol:toCol,
					fromRow:candy.row,
					fromCol:candy.col,
				});
			}
		}
	};

	 /**
	* Remove a candy from this board.
	* Requires candy to be found on this board.
	*/
	const remove = (candy) => {
		if(squareRef.current[candy.row]!= undefined && squareRef.current[candy.row][candy.col]!= undefined){
			delete squareRef.current[candy.row][candy.col];
			const details = {
				candy: candy,
				fromRow: candy.row,
				fromCol: candy.col
			};
			candy.col = candy.row = null;
			removeAnimate(details);
		}
		///$(this).triggerHandler("remove", details);//-------------------------------------------------------------->MOVEMENT
	};

	const removeAnimate = (info) => {
		const layer = refs.current['layer'];
		let candy = info.candy;
		let rect = refs.current[`candy_${candy.id}`];

		if(rect) {
			rect.to({
				scaleX:0.2,
				scaleY:0.2,
				opacity:0.3,
				rotation:240 * Math.random() - 120,
				duration:0.3,
				easing:Konva.Easings.StrongEaseOut,
				onFinish:() => {
					delete refs.current[`candy_${candy.id}`];
					delete squareRef.current[info.fromRow][info.fromCol];
					rect.remove();
					rect.destroy();
					if(layer!=undefined)layer.draw();
				},
			});
		}
	};

	/*
	Adds a candy of specified color to row, col.
	*/
	const addCandy = (color, row, col, spawnRow, spawnCol) => {
		add(createCandy(color), row, col, spawnRow, spawnCol);
	};

	/**
	* Adds a candy of random color at row, col.
	*/
	const addRandomCandy = (row, col, spawnRow, spawnCol) => {
		add(createCandy(), row, col, spawnRow, spawnCol);
	};

	/**
	*  color index a colors[]
	*/
	const createCandy = (index_color) => {
		candy_index.current++;
		const color = (index_color == undefined) ? colors[Math.floor(Math.random() * colors.length)].id : colors[index_color].id;
		return { color:color, id:candy_index.current, row:0, col:0 };
	};

	/*
	Returns the candy immediately in the direction specified by direction
	['up', 'down', 'left', 'right'] from the candy passed as fromCandy
	*/
	const getCandyInDirection = (fromCandy, direction) => {
		switch(direction) {
			case 'up':
				return getCandyAt(fromCandy.row - 1, fromCandy.col);

			case 'down':
				return getCandyAt(fromCandy.row + 1, fromCandy.col);

			case 'left':
				return getCandyAt(fromCandy.row, fromCandy.col - 1);

			case 'right':
				return getCandyAt(fromCandy.row, fromCandy.col + 1);
		}
	};

	/* Flip candy1 with candy2 in one step, firing two move events.
	 * Does not verify the validity of the flip. Does not crush candies
	 * produced by flip. */
	const flipCandies = (candy1, candy2) => {
		// Swap the two candies simultaneously.
		const details1 = {
			candy:candy1,
			toRow:candy2.row,
			toCol:candy2.col,
			fromRow:candy1.row,
			fromCol:candy1.col
		};

		const details2 = {
			candy:candy2,
			toRow:candy1.row,
			toCol:candy1.col,
			fromRow:candy2.row,
			fromCol:candy2.col
		};

		candy1.row = details1.toRow;
		candy1.col = details1.toCol;
		squareRef.current[details1.toRow][details1.toCol] = candy1;

		candy2.row = details2.toRow;
		candy2.col = details2.toCol;
		squareRef.current[details2.toRow][details2.toCol] = candy2;

		// Trigger two move events.
		moveAnimate(details1);
		moveAnimate(details2);
		doHit();
	};

	const moveAnimate = (info) => {
		const layer = refs.current['layer'];

		let candy = info.candy;
		let rect = refs.current['candy_' + candy.id];
		var top = info.toRow * candy_size.current;
		var left = info.toCol * candy_size.current;
		if(rect) {
			moving.current++;

			rect.to({
				x:left + candy_size.current / 2,
				y:top + candy_size.current / 2,
				easing:Konva.Easings.BounceEaseOut,
				duration:stroke_anim_duration,
				onFinish:() => {
					moving.current--;
					if(moving.current == 0) Crush();
				},
			});

			if(layer!=undefined)layer.draw();
		}
	};

	const setToDraggable = (draggable,candy_id)=>{
		(refs.current['layer'].getChildren()).map((ref) => {
			if(candy_id != undefined && ref.attrs.id != undefined && candy_id != ref.attrs.id && (ref.attrs.id).indexOf('candy_') >= 0) {
				ref.setAttrs({ draggable:draggable });
			} else if(candy_id == undefined && ref.attrs.id != undefined && (ref.attrs.id).indexOf('candy_') >= 0) {
				ref.setAttrs({ draggable:draggable });
			}
		});
	};

	const incrementScore = (candy) => {
		let tmp_successful = successful;
		let multiplier = last_combo.current;
		candy.multiplier = multiplier;
		tmp_successful.push(candy);
		setSuccessful(tmp_successful);
	};

	const isCandyHinted = (candy_id) => {
		if(hinted_candies.current == null || hinted_candies.current.length == 0) return false;

		const is_candy_in_hint = hinted_candies.current.find((c) => { return c.id == candy_id });
		return (is_candy_in_hint != undefined) ? true : false;
	}

	const hintAnimation = (candy_id, is_up, round) => {
		const _ref_candy = refs.current[`candy_${candy_id}`];
		
		if(is_up == undefined) is_up = true;
		if(round == undefined) round = 0;
		const max_rounds = 5;
		const scale = (is_up) ? 1.15 : 1;
		
		if(_ref_candy == undefined || !isCandyHinted(candy_id)) return;
		_ref_candy.to({
			scaleX:scale,
			scaleY:scale,
			easing:Konva.Easings.StrongEaseInOut,
			duration:0.22,
			onFinish:() => {
				if(round < max_rounds) {
					hintAnimation(candy_id, !is_up, round + 1);
				} else {
					display_hint.current = setTimeout(() => {
						if(_ref_candy != undefined) {
							hintAnimation(candy_id);
						} else {
							finishHintAnimation();
						}
					}, 5000);
				}
			},
		});
	};

	const displayHint = () => {
		if(!is_playable) return;

		finishHintAnimation();
		display_hint.current = setTimeout(() => {
			const hint = getRandomValidMove();

			let candies_to_hint = [];
			if(hint_group) {
				candies_to_hint = hint;
			} else {
				candies_to_hint.push(hint.candy);
			}

			hinted_candies.current = candies_to_hint;

			moveToTopZ(hinted_candies.current);

			hinted_candies.current.map((candy) => {
				hintAnimation(candy.id);
			});

			soundHint();
		}, 5000);
	};

	const moveToTopZ = (candies_arr) => {
		let candies_ids_arr = [];
		candies_arr.map((c) => {
			candies_ids_arr.push(`candy_${c.id}`);
		});

		const candies_not_in_hint = refs.current['candies_group'].getChildren().filter((o) => {
			return !candies_ids_arr.includes(o.children[0].attrs.id);
		});
		candies_not_in_hint.map((candy, c) => {
			candy.zIndex(c);
		});
		candies_arr.map((candy, c) => {
			refs.current[`candy_${candy.id}`].zIndex(candies_not_in_hint.length + c);
		});
	};

	const finishHintAnimation = () => {
		clearTimeout(display_hint.current);

		if(hinted_candies.current != undefined && hinted_candies.current.length > 0) {
			hinted_candies.current.map((candy) => {
				if(refs.current[`candy_${candy.id}`] == undefined) return;

				refs.current[`candy_${candy.id}`].to({
					scaleX:1,
					scaleY:1,
					duration:0.1,
				});
			});

			hinted_candies.current = null;
		}
	};

	const generateStrokeLine = (combo, av_color) => {
		if(av_color == undefined) av_color = { r:0, g:0, b:0 };

		if(combo.points == undefined || combo.points === false) return false;

		let points = [];
		combo.points.map((point) => {
			points.push(point * candy_size.current + candy_size.current / 2);
		});

		const line_length = (combo.length != undefined) ? combo.length * candy_size.current : 100;

		return new Konva.Line({
			points:points,
			strokeWidth:Math.round(0.8 * candy_size.current),
			stroke:`rgba(${av_color.r}, ${av_color.g}, ${av_color.b}, 0.7)`,
			lineJoin:'round',
			lineCap:'round',
			dash:[line_length, line_length],
			dashOffset:line_length,
		});
	};

	const detectComboShape = (c) => {
		let min_row = board_size + 1;
		let min_col = board_size + 1;
		let max_row = -1;
		let max_col = -1;

		c.map((candy) => {
			if(candy.row < min_row) min_row = candy.row;
			if(candy.col < min_col) min_col = candy.col;
			if(candy.row > max_row) max_row = candy.row;
			if(candy.col > max_col) max_col = candy.col;
		});

		let line_length = Math.max(1, max_row - min_row) * Math.max(1, max_col - min_col);

		if(min_row == max_row || min_col == max_col) {
			return { type:'line', points:[min_col, min_row, max_col, max_row], length:line_length, multiplier:multiplier_linecombo };
		}

		// L-shaped combos (orientation indicates the angle corner):
		if(c[0].row == min_row && c[0].col == c[c.length - 1].col && c[Math.ceil(c.length / 2)].col == min_col) {
			return { type:'Ltl', points:[max_col, min_row, min_col, min_row, min_col, max_row], length:line_length, multiplier:multiplier_ltcombo };
		}

		if(c[0].row == min_row && c[Math.ceil(c.length / 2)].row == max_row && c[c.length - 1].col == max_col && c[0].col == c[2].col) {
			return { type:'Lbl', points:[min_col, min_row, min_col, max_row, max_col, max_row], length:line_length, multiplier:multiplier_ltcombo };
		}

		if(c[0].row == min_row && c[Math.floor(c.length / 2)].row == min_row && c[c.length - 1].col == max_col && c[0].row == c[2].row) {
			return { type:'Ltr', points:[min_col, min_row, max_col, min_row, max_col, max_row], length:line_length, multiplier:multiplier_ltcombo };
		}

		if(c[0].col == max_col && c[Math.ceil(c.length / 2)].row == max_row && c[c.length - 1].col == max_col) {
			return { type:'Lbr', points:[max_col, min_row, max_col, max_row, min_col, max_row], length:line_length, multiplier:multiplier_ltcombo };
		}


		// T-shaped combos (orientation indicates the short stroke side):
		if(c[0].row == min_row && c[0].row == c[1].row && min_row != max_row) {
			line_length += max_col - c[c.length - 1].col;
			return { type:'Tt', points:[min_col, min_row, max_col, min_row, c[c.length - 1].col, min_row, c[c.length - 1].col, max_row], length:line_length, multiplier:multiplier_ltcombo };
		}

		if(c[0].col == max_col && c[0].col == c[c.length - 1].col && min_col != max_col) {
			line_length += c[Math.ceil(c.length / 2)].row - min_row;
			return { type:'Tr', points:[min_col, c[Math.ceil(c.length / 2)].row, max_col, c[Math.ceil(c.length / 2)].row, max_col, min_row, max_col, max_row], length:line_length, multiplier:multiplier_ltcombo };
		}

		if(c[c.length - 1].row == max_row && c[c.length - 1].row == c[c.length - 2].row && min_row != max_row) {
			line_length += max_col - c[0].col;
			return { type:'Tb', points:[c[0].col, min_row, c[0].col, max_row, max_col, max_row, min_col, max_row], length:line_length, multiplier:multiplier_ltcombo };
		}

		if(c[0].col == min_col && c[0].col == c[1].col && min_col != max_col) {
			line_length += max_row - c[Math.ceil(c.length / 2)].row;
			return { type:'Tl', points:[min_col, min_row, min_col, max_row, min_col, c[Math.ceil(c.length / 2)].row, max_col, c[Math.ceil(c.length / 2)].row], length:line_length, multiplier:multiplier_ltcombo };
		}

		if(c[0].col != min_col && c[0].col != max_col && c[c.length - 1] != min_col && c[c.length - 1] != max_col) {
			return { type:'Tc', points:[c[0].col, c[0].row, c[c.length - 1].col, c[c.length - 1].row, c[0].col, c[1].row, c[1].col, c[1].row, max_col, c[1].row], length:line_length, multiplier:multiplier_crosscombo };
		}

		return { type:'weird', points:false };
	};

	const addScoreText = (score, points) => {
		const layer = refs.current['layer_text'];
		if(layer == undefined) return;

		const x_ini = points[points.length - 2];
		const y_ini = points[points.length - 1];

		const font_size = 20 * size.current / standard_board_size;

		let score_text_props = {
			text:`+${score}`,
			width:candy_size.current,
			height:font_size,
			fontSize:font_size,
			fontStyle:'bold',
			lineHeight:1,
			align:'center',
			fill:'black',
		};

		const score_text_1 = new Konva.Text({ ...score_text_props, stroke:'white', strokeWidth:3, strokeLineCap:'round' });
		const score_text_2 = new Konva.Text({ ...score_text_props });

		let score_text_group = new Konva.Group({
			x:x_ini - candy_size.current / 2,
			y:y_ini - (font_size / 2),
		});
		score_text_group.add(score_text_1).add(score_text_2);

		const text_index = generateUUID();
		refs.current[`score_text_${text_index}`] = score_text_group;
		refs.current[`score_text_${text_index}`].cache(default_cache_props);
		if(layer != undefined) layer.add(score_text_group).draw();

		if(score_text_group != undefined) {
			score_text_group.to({
				y:y_ini - (font_size / 2) - candy_size.current,
				duration:1.3,
				opacity:0.3,
				easing:Konva.Easings.StrongEaseInOut,
				onFinish:() => {
					score_text_group.to({
						duration:0.3,
						opacity:0,
						easing:Konva.Easings.StrongEaseInOut,
						onFinish:() => {
							refs.current[`score_text_${text_index}`].destroy();
							if(layer!=undefined)layer.draw();
						},
					});
				},
			});
		}
	};

	const getLimitsOfCrush = (actual_combo) => {
		const combos_to_remove = [...actual_combo];

		combos_to_remove.map((combo, c) => {
			let stroke_line = [];

			const combo_shape = detectComboShape(combo);
			if(combo_shape.type != 'weird') {
				last_combo.current = combo_shape.multiplier;
				const score_in_combo = combo.length * parseInt(colors.find((obj) => { return combo[0].color == obj.id })?.points ?? '10') * last_combo.current;

				const av_color = items.find((item) => item.identifier == combo[0].color).av_color;

				const new_line = generateStrokeLine(combo_shape, av_color);
				addScoreText(score_in_combo, new_line.attrs.points);
				stroke_line.push(new_line);

				if(combo_shape.type != 'line') {
					soundCross();
					addTextEffect(combo_shape.type);
				} else if(c == 0) {
					soundSlide();
				}

				if(stroke_line.length > 0) {
					stroke_line.map((_stroke, s) => {
						if(stroke_line !== false) {
							if(refs.current['layer_fx'] != undefined) {
								refs.current['layer_fx'].add(_stroke);
								refs.current['layer_fx'].draw();
							}
							refs.current[`stroke_line_${c}_${s}`] = _stroke;
							animateCrushStroke(`${c}_${s}`);
						}
					});
				}
			}
		});
	};

	const addTextEffect = (type) => {
		const layer = refs.current['layer_text'];

		let anim_delay = 0;

		let text_label = "";
		switch(type) {
			case 'Ltl':
			case 'Lbl':
			case 'Ltr':
			case 'Lbr':
				text_label = "L COMBO";
				break;

			case 'Tt':
			case 'Tr':
			case 'Tb':
			case 'Tl':
				text_label = "T COMBO";
				break;

			case 'Tc':
				text_label = "CROSS COMBO";
				break;
			
			case 'GAME_ENDED':
				text_label = "NO MORE MOVES AVAILABLE";
				anim_delay = 3;
				break;
		}

		const font_size = 50 * size.current / standard_board_size;

		let combo_text = refs.current['combo_text'];
		if(combo_text == undefined) {
			const combo_text_props = {
				text:text_label,
				width:size.current,
				height:size.current,
				fontSize:font_size,
				fontStyle:'bold',
				lineHeight:1,
				align:'center',
				fill:'black',
			};

			let combo_text_stroke = new Konva.Text({
				stroke:'white',
				strokeWidth:4,
				lineCap:'round',
				lineJoin:'round',
				...combo_text_props,
			});
			let combo_text_txt = new Konva.Text(combo_text_props);

			combo_text = new Konva.Group({
				x:size.current / 2,
				y:(size.current - Math.floor(font_size / 2)) / 2,
				scaleX:0,
				scaleY:0,
				offsetX:size.current / 2,
				offsetY:font_size / 2,
			});
			combo_text.add(combo_text_stroke);
			combo_text.add(combo_text_txt);

			refs.current['combo_text'] = combo_text;
			refs.current['combo_text_text'] = combo_text_txt;
			refs.current['combo_text_stroke'] = combo_text_stroke;
			refs.current['combo_text'].cache(default_cache_props);

			if(layer != undefined) layer.add(combo_text).draw();
		} else {
			refs.current['combo_text_text'].text(text_label);
			refs.current['combo_text_stroke'].text(text_label);
			if(refs.current['combo_text'] != undefined) refs.current['combo_text'].clearCache();
			refs.current['combo_text'].cache(default_cache_props);
		}

		combo_text.to({
			scaleX:1.2,
			scaleY:1.2,
			duration:1,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				setTimeout(() => {
					combo_text.to({
						scaleX:0,
						scaleY:0,
						duration:1,
						easing:Konva.Easings.StrongEaseInOut,
					});
				}, anim_delay * 1000);
			},
		});
	};

	const animateCrushStroke = (index) => {
		let stroke = refs.current[`stroke_line_${index}`];
		if(stroke == undefined) return;

		stroke.to({
			dashOffset:-stroke.attrs.dash[0],
			duration:stroke_anim_duration * 1.2,
			opacity:0.3,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				stroke.destroy();
				if(refs.current['layer_fx'] != undefined)refs.current['layer_fx'].draw();
				delete refs.current[`stroke_line_${index}`];
			},
		});
	};

	const getValidMoves = () => {
		let total = 0;
		const directions = ['up', 'down', 'left', 'right'];
		for(let row = 0; row < board_size; row++) {
			for(let col = 0; col < board_size; col++) {
				const fromCandy = getCandyAt(row, col);
				if(!fromCandy) continue;

				for(let i = 0; i < 4; i++) {
					const direction = directions[i];
					const candiesCrushed = numberCandiesCrushedByMove(fromCandy, direction);
					const numCandiesCrushed = candiesCrushed.length;
					if(numCandiesCrushed >= 3) total++;
				}
			}
		}

		return total;
	};

	/* Handler Functions */
	const Crush = () => {
		if(stoppedGame.current) {
			is_idle.current = true;
			return;
		}

		const pending_crushes = getCandyCrushes();
		getLimitsOfCrush(pending_crushes);
		finishHintAnimation();

		const has_ended_animations = (moving.current == 0 && pending_crushes.length == 0) ? true : false;
		is_idle.current = false;

		setTimeout(() => {
			moveCandiesDown();

			props.onScore(getPoints());
			if(has_ended_animations) {
				blockMovements(false);
				let possible_movements = getValidMoves();
				if(stage.crush_min == hitsRef.current || !possible_movements) {
					if(!possible_movements && props.forceStop != undefined) props.forceStop();
					if(!possible_movements) addTextEffect('GAME_ENDED');
					finishHintAnimation();
					is_idle.current = true;
					endGame(true);
					return;
				} else {
					is_idle.current = true;
					displayHint();
				}
			}
		}, (!has_ended_animations ? (stroke_anim_duration) : 0) * 1000);

		removeCrushes(pending_crushes);
		if(refs.current['movements_blocker'] == undefined) blockMovements(true);
	};

	const removeBlocker = () => {
		if(refs.current['movements_blocker'] == undefined) return;
		refs.current['movements_blocker'].destroy();
		delete(refs.current['movements_blocker']);
		if(refs.current['layer_blocker'] != undefined) refs.current['layer_blocker'].draw();
	};

	const blockMovements = (block) => {
		if(block == undefined) block = true;
		if(block) {
			const blocker = new Konva.Rect({
				width:size.current,
				height:size.current,
				fill:`rgba(255, 255, 255, ${((debug) ? '0.5' : '0')})`,
			});
			refs.current['movements_blocker'] = blocker;

			if(refs.current['layer_blocker'] != undefined) refs.current['layer_blocker'].add(blocker).draw();
		} else {
			removeBlocker();
		}
	};

	const handleDragStart = (e) => {
		if(!is_playable || moving.current > 0) return;
		const layer = refs.current['layer'];

		finishHintAnimation();
		const attrs = e.target.attrs;
		actual.current = attrs;
		let rect = getCurrentCandy(attrs.id);
		rect.scale({ x:1.2, y:1.2 });
		rect.opacity(0.5);

		addIntersectors(e);

		rect.zIndex(Math.pow(board_size, 2) - 1);
		if(layer != undefined) layer.draw();
	};

	const haveIntersection = (r1, r2) => {
		return !(
			r2.x > r1.x + r1.width ||
			r2.x + r2.width < r1.x ||
			r2.y > r1.y + r1.height ||
			r2.y + r2.height < r1.y
		);
	}

	const getCurrentCandy = (candy_id) => {
		return refs.current[candy_id];
	};

	const handleCollisions = (e, candy_start) => {
		let direction = false;
		let correct_movement = false;

		refs.current['collision_detector'].children.forEach((group) => {
			if(haveIntersection(group.getClientRect(), e.currentTarget.getClientRect())) {
				const _dir = group.attrs.direction;

				if(isMoveTypeValid(candy_start, _dir)) {
					if(debug) {
						group.setAttrs({
							fill:'black',
						});
						refs.current['layer'].draw();
					}
					correct_movement = true;
					direction = _dir;
				}
			}
		});

		return {
			direction,
			correct_movement,
		};
	};

	const getCandyById = (id) => {
		let candy;
		for(let y = 0; y < board_size; y++) {
			for(let x = 0; x < board_size; x++) {
				if(squareRef.current[x][y] != undefined && squareRef.current[x][y].id == id) {
					candy = squareRef.current[x][y];
				}
			}
		}
		return candy;
	};

	const handleDragEnd = (e) => {
		if(!is_playable || e.target.parent == undefined) return;
		
		finishHintAnimation();
		
		const candy_start = actual.current;
		
		if(candy_start) {
			blockMovements(true);
			setToDraggable(false, candy_start.id);

			const candy_id = (candy_start.id).split('_');
			let candy = getCandyById(candy_id[1]);

			const candy_collision = handleCollisions(e, candy);
			
			if(candy_collision.correct_movement) {
				flipCandies(candy, getCandyInDirection(candy, candy_collision.direction));
			}

			let rect = getCurrentCandy(candy_start.id);
			if(!candy_collision.correct_movement) {
				rect.to({
					x:candy.col * candy_size.current + candy_size.current / 2,
					y:candy.row * candy_size.current + candy_size.current / 2,
					opacity:1,
					scaleX:1,
					scaleY:1,
					onFinish:() => {
						rect.zIndex(1);
					},
				});

				blockMovements(false);
				if(refs.current['layer'] != undefined) refs.current['layer'].draw();
				//soundError();
			} else {
				rect.to({ scaleX:1, scaleY:1, opacity:1 });
			}

			if(actual_over.current) {
				refs.current['candy_' + actual_over.current.id].opacity(1);
				actual_over.current = null;
			}
			actual.current = null;
		}

		if(debug) {
			setTimeout(() => {
				removeIntersectors();
			}, 1000);
		} else {
			removeIntersectors();
		}
	};

	const addIntersectors = (actual) => {
		removeIntersectors();

		const actual_moving = getCandyById(actual.currentTarget.getChildren()[0].attrs.id.split("_")[1]);
		
		let intersectors = [];
		if(actual_moving.col > 0) intersectors.push({ row:0, col:-1, dir:'left' });
		if(actual_moving.row > 0) intersectors.push({ row:-1, col:0, dir:'up' });
		if(actual_moving.col < board_size - 1) intersectors.push({ row:0, col:1, dir:'right' });
		if(actual_moving.row < board_size - 1) intersectors.push({ row:1, col:0, dir:'down' });

		const collision_detector = new Konva.Group();
		refs.current['helpers_layer'].add(collision_detector);
		refs.current['collision_detector'] = collision_detector;

		const detector_size = candy_size.current / 2;

		intersectors.map((int, i) => {
			const _row = actual_moving.row + int.row;
			const _col = actual_moving.col + int.col;
			const init_x = candy_size.current * _col;
			const init_y = candy_size.current * _row;

			const intersector = new Konva.Rect({
				key:`detector_${i}`,
				x:init_x + (candy_size.current - detector_size) / 2,
				y:init_y + (candy_size.current - detector_size) / 2,
				width:detector_size,
				height:detector_size,
				fill:`rgba(0, 0, 255, ${((debug) ? '1' : '0')})`,
				col:_col,
				row:_row,
				direction:int.dir,
			});

			refs.current['collision_detector'].add(intersector);
		});
		refs.current['layer'].draw();
	}

	const removeIntersectors = () => {
		if(refs.current['collision_detector'] != undefined) {
			refs.current['collision_detector'].destroy();
			refs.current['layer'].draw();
		}
	}

	const doHit = () => {
		if(!is_builder || is_test) props.doHit();
	};

	const getPoints = () => {
		let points = 0;
		(successful).map((candy) => {
			let cand = (stage.crush_images).find((image) => (image.id == candy.color));
			points += (candy.multiplier != undefined) ? parseInt(cand.points) * candy.multiplier : parseInt(cand.points);
		});

		return points;
	};

	const endGame = (success_game) => {
		//setGameFinished(true);
		blockMovements(true);
		finishHintAnimation();
		const layer = refs.current['candies_group'];
		const total_candies_in_board = layer.getChildren().length;
		layer.getChildren().map((candy, c) => {
			setTimeout(() => {
				if(candy == undefined) return;

				candy.to({
					y:candy.attrs.y + size.current,
					duration:1,
					opacity:0.8,
					easing:Konva.Easings.StrongEaseIn,
					onFinish:() => {
						candy.destroy();
						if(c < 1) {
							props.onEnded(success_game);
						}
					}
				});
			}, 25 * (total_candies_in_board - c));
		});
	};

	let board = [];
	for(var r = 0; r < board_size; ++r) board.push(r);

	const main_classes = ['canvas_cont'];
	if(display_outter_border === false) main_classes.push('not_bordered');

	if(!full_loaded) {
		return (
			<div class={main_classes.join(" ")}>
				<Spinner>Loading game</Spinner>
			</div>
		)
	}

	return (
		<Stage
			ref={(node) => { refs.current[`main_stage`] = node; }}
			className={main_classes.join(" ")}
			width={size.current}
			height={size.current}
		>
			<Layer ref={(node) => { refs.current[`layer_fx`] = node; }}>
				<Rect
					width={size.current}
					height={size.current}
					fill={stage?.crush_bg_color ?? '#FFFFFF'}
				/>
			</Layer>

			<Layer ref={(node) => { refs.current[`layer`] = node; }}>
				<Group ref={(node) => { refs.current[`candies_group`] = node; }}>
					{squareRef.current.length > 0 && squareRef.current.map((row) => {
						return (
							row.map((sq) => {
								const init_x = candy_size.current * sq.col;
								const init_y = candy_size.current * sq.row;
								const image = items.find((item) => item.identifier == sq.color);
								const slide_size = candy_size.current / (Math.max(image.image_w, image.image_h));

								return (
									<Group
										key={`candy_${sq.id}`}
										ref={(node) => { refs.current[`candy_${sq.id}`] = node; }}
										scale={{ x:1, y:1 }}
										draggable={is_playable}
										onMouseUp={handleDragEnd}
										onTouchEnd={handleDragEnd}
										onDragEnd={handleDragEnd}
										onMouseDown={handleDragStart}
										onTouchStart={handleDragStart}
										x={init_x + candy_size.current / 2}
										y={init_y + candy_size.current / 2}
										width={candy_size.current}
										height={candy_size.current}
										offsetX={candy_size.current / 2}
										offsetY={candy_size.current / 2}
									>
										<Rect
											width={candy_size.current}
											height={candy_size.current}
											id={`candy_${sq.id}`}
											fillPatternImage={image.fillPatternImage}
											fillPatternRepeat='no-repeat'
											fillPatternScaleX={slide_size}
											fillPatternScaleY={slide_size}
										/>
									</Group>
								)
							})
						)
					})}
				</Group>

				<Group ref={(node) => { refs.current[`helpers_layer`] = node; }} />
			</Layer>

			<Layer>
				<Group ref={(node) => { refs.current[`layer_text`] = node; }} />

				<Group ref={(node) => { refs.current[`layer_blocker`] = node; }} />
			</Layer>
		</Stage>
	)
})

export default CrushCanvas;